import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useToast } from '../hooks/useToast'

interface Permission {
  id: string
  module: string
  action: string
  description: string
}

interface Role {
  id: string
  name: string
  description: string
  isDefault: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  rolePermissions: Array<{
    id: string
    granted: boolean
    permission: Permission
  }>
}

interface PermissionMatrix {
  [module: string]: {
    [action: string]: boolean
  }
}

const RoleManagement: React.FC = () => {
  const { showSuccess, showError } = useToast()
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  })
  const [permissionMatrix, setPermissionMatrix] = useState<PermissionMatrix>({})

  useEffect(() => {
    fetchRoles()
    fetchPermissions()
  }, [])

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/api/roles?all=true', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      if (response.data.success) {
        setRoles(response.data.data)
      }
    } catch (error: any) {
      showError('Failed to fetch roles')
      console.error('Error fetching roles:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/api/roles/permissions', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      if (response.data.success) {
        setPermissions(response.data.data)
        initializePermissionMatrix(response.data.data)
      }
    } catch (error: any) {
      console.error('Error fetching permissions:', error)
    }
  }

  const initializePermissionMatrix = (perms: Permission[]) => {
    const matrix: PermissionMatrix = {}
    perms.forEach(permission => {
      if (!matrix[permission.module]) {
        matrix[permission.module] = {}
      }
      matrix[permission.module][permission.action] = false
    })
    setPermissionMatrix(matrix)
  }

  const loadRolePermissions = (role: Role) => {
    const matrix: PermissionMatrix = {}
    role.rolePermissions.forEach(rp => {
      if (!matrix[rp.permission.module]) {
        matrix[rp.permission.module] = {}
      }
      matrix[rp.permission.module][rp.permission.action] = rp.granted
    })
    setPermissionMatrix(matrix)
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const rolePermissions = Object.entries(permissionMatrix).flatMap(([module, actions]) =>
        Object.entries(actions).map(([action, granted]) => ({
          permissionId: permissions.find(p => p.module === module && p.action === action)?.id,
          granted
        })).filter(rp => rp.permissionId)
      )

      const response = await axios.post('/api/roles', {
        ...formData,
        rolePermissions
      })
      
      if (response.data.success) {
        showSuccess('Role created successfully')
        setShowCreateForm(false)
        setFormData({ name: '', description: '' })
        initializePermissionMatrix(permissions)
        fetchRoles()
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to create role')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingRole) return

    try {
      const rolePermissions = Object.entries(permissionMatrix).flatMap(([module, actions]) =>
        Object.entries(actions).map(([action, granted]) => ({
          permissionId: permissions.find(p => p.module === module && p.action === action)?.id,
          granted
        })).filter(rp => rp.permissionId)
      )

      const response = await axios.put(`/api/roles/${editingRole.id}`, {
        ...formData,
        rolePermissions
      })
      
      if (response.data.success) {
        showSuccess('Role updated successfully')
        setEditingRole(null)
        setFormData({ name: '', description: '' })
        initializePermissionMatrix(permissions)
        fetchRoles()
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to update role')
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return

    try {
      const response = await axios.delete(`/api/roles/${id}`)
      if (response.data.success) {
        showSuccess('Role deleted successfully')
        fetchRoles()
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to delete role')
    }
  }

  const handleToggleActive = async (role: Role) => {
    try {
      const response = await axios.put(`/api/roles/${role.id}`, {
        ...role,
        isActive: !role.isActive
      })
      if (response.data.success) {
        showSuccess(`Role ${!role.isActive ? 'activated' : 'deactivated'} successfully`)
        fetchRoles()
      }
    } catch (error: any) {
      showError('Failed to update role status')
    }
  }

  const openEditForm = (role: Role) => {
    setEditingRole(role)
    setFormData({
      name: role.name,
      description: role.description || ''
    })
    loadRolePermissions(role)
  }

  const closeForms = () => {
    setShowCreateForm(false)
    setEditingRole(null)
    setFormData({ name: '', description: '' })
    initializePermissionMatrix(permissions)
  }

  const togglePermission = (module: string, action: string) => {
    setPermissionMatrix(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module]?.[action]
      }
    }))
  }

  const toggleAllModulePermissions = (module: string, granted: boolean) => {
    setPermissionMatrix(prev => ({
      ...prev,
      [module]: Object.keys(prev[module] || {}).reduce((acc, action) => ({
        ...acc,
        [action]: granted
      }), {})
    }))
  }

  const getModulePermissions = (module: string) => {
    return permissions.filter(p => p.module === module)
  }

  const getUniqueModules = () => {
    return [...new Set(permissions.map(p => p.module))]
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Role Management</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Create Role
          </button>
        </div>

        {/* Create/Edit Form */}
        {(showCreateForm || editingRole) && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg font-medium mb-4">
              {editingRole ? 'Edit Role' : 'Create New Role'}
            </h2>
            <form onSubmit={editingRole ? handleUpdate : handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>

              {/* Permission Matrix */}
              <div className="mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4">Permissions</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Module
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Actions
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          All
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getUniqueModules().map((module) => (
                        <tr key={module}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r">
                            {module.charAt(0).toUpperCase() + module.slice(1)}
                          </td>
                          <td className="px-4 py-3 border-r">
                            <div className="flex flex-wrap gap-2">
                              {getModulePermissions(module).map((permission) => (
                                <label key={permission.id} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={permissionMatrix[module]?.[permission.action] || false}
                                    onChange={() => togglePermission(module, permission.action)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="text-sm text-gray-700">{permission.action}</span>
                                </label>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center space-x-2">
                              <button
                                type="button"
                                onClick={() => toggleAllModulePermissions(module, true)}
                                className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded hover:bg-green-200"
                              >
                                All
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleAllModulePermissions(module, false)}
                                className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200"
                              >
                                None
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeForms}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingRole ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Roles List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {roles.map((role) => (
              <li key={role.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">{role.name}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        role.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {role.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {role.isDefault && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          Default
                        </span>
                      )}
                    </div>
                    {role.description && (
                      <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                    )}
                    <div className="mt-2 text-sm text-gray-500">
                      <span>🔐 {role.rolePermissions.filter(rp => rp.granted).length} permissions granted</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditForm(role)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(role)}
                      className={`text-sm font-medium ${
                        role.isActive 
                          ? 'text-red-600 hover:text-red-800' 
                          : 'text-green-600 hover:text-green-800'
                      }`}
                    >
                      {role.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    {!role.isDefault && (
                      <button
                        onClick={() => handleDelete(role.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {roles.length === 0 && (
            <div className="px-6 py-12 text-center text-gray-500">
              No roles found. Create your first one above.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RoleManagement
